import { useState, useCallback, useEffect, useRef } from "react";

import { config } from "../../config";
import { useHttpClient } from "../Hooks/http-hook";
import dedupeStoreFetch from "../util/dedupe-store-fetch";

const useStoreDetails = props => {
    const [storeDetails, setStoreDetails] = useState(null);
    const [isUpdatedStoreDetails, setIsUpdatedStoreDetails] = useState(false);
    const [HideStoreDetailsSkeleton, setHideStoreDetailsSkeleton] = useState(false);
    const [storeUN, setStoreUN] = useState('');
    const { error, sendRequest, clearError } = useHttpClient();
    const cacheRef = useRef({
        isUpdatedStoreDetails: false,
        storeUN: '',
        storeDetails: null,
    });
    const pendingFetchesRef = useRef(new Map());

    cacheRef.current = {
        isUpdatedStoreDetails,
        storeUN,
        storeDetails,
    };

    const updateStoreDetails = (newStoreDetails) => {
        setStoreDetails(newStoreDetails);
        setIsUpdatedStoreDetails(true);
    };

    const fetchStoreDetails = useCallback(async (username, { force = false } = {}) => {
        return dedupeStoreFetch({
            key: username,
            force,
            cacheRef,
            pendingRef: pendingFetchesRef,
            isCacheHit: (cache) => (
                cache.isUpdatedStoreDetails && cache.storeUN === username
            ),
            getCachedValue: (cache) => cache.storeDetails,
            runRequest: async () => {
                setStoreUN(username);
                try {
                    const responseData = await sendRequest(
                        `${config.API_URL}/storeDetails/${username}`
                    );
                    clearError();
                    updateStoreDetails(responseData);
                    return responseData;
                } catch (err) {
                    setIsUpdatedStoreDetails(true);
                    setStoreUN(username);
                    console.error(err);
                    return null;
                }
            },
        });
    }, [sendRequest, clearError]);

    const reloadStoreDetails = useCallback(
        (username) => fetchStoreDetails(username, { force: true }),
        [fetchStoreDetails]
    );

    const removeStoreDetails = useCallback(() => {
        pendingFetchesRef.current.clear();
        setStoreDetails(null);
        setStoreUN('');
        setIsUpdatedStoreDetails(false);
        setHideStoreDetailsSkeleton(false);
    }, [setHideStoreDetailsSkeleton]);

    useEffect(() => {
        const storedData = JSON.parse(localStorage.getItem('storec-userData'));
        if (storedData && storedData.username && props.role === "store" && !isUpdatedStoreDetails) {
            fetchStoreDetails(storedData.username);
            setTimeout(() => {
                setHideStoreDetailsSkeleton(true);
            }, 1000);
        }
    }, [props.role]);

    return {
        storeDetails,
        isUpdatedStoreDetails, 
        fetchStoreDetails,
        reloadStoreDetails,
        HideStoreDetailsSkeleton,
        StoreDetailsError: error,
        removeStoreDetails,
        setHideStoreDetailsSkeleton,
        storeUN,
        setStoreUN,
    };
}

export default useStoreDetails;
